import { useRef, useState } from "react";
import { niceMax } from "./format";
import { ChartTooltip } from "./ChartTooltip";
import { useChartWidth } from "./useChartWidth";

const VIEW_H = 220;
const MARGIN = { top: 16, right: 56, bottom: 44, left: 64 };
const INNER_H = VIEW_H - MARGIN.top - MARGIN.bottom;
const END_LABEL_GAP = 16;

// A line chart in plain SVG — 2px lines, an end-dot per series, a crosshair
// that snaps to the nearest X and a tooltip listing every series at that X
// (per the dataviz skill's interaction spec), plus a table-view fallback.
export const LineChart = ({ data, seriesKeys, title, valueFormatter = (v) => v, allowNegative = false }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const containerRef = useRef(null);
  const viewW = useChartWidth(containerRef);
  const innerW = viewW - MARGIN.left - MARGIN.right;

  if (!data || data.length === 0) {
    return <p className="py-8 text-sm text-center text-gray-500">No data for this range.</p>;
  }

  const allValues = data.flatMap((row) => seriesKeys.map((s) => row[s.key] || 0));
  const dataMax = Math.max(0, ...allValues);
  const dataMin = allowNegative ? Math.min(0, ...allValues) : 0;
  const maxValue = niceMax(Math.max(1, dataMax));
  const minValue = allowNegative && dataMin < 0 ? -niceMax(Math.abs(dataMin)) : 0;
  const span = maxValue - minValue;

  const yFor = (value) => INNER_H - ((value - minValue) / span) * INNER_H;
  const xFor = (i) => (data.length === 1 ? innerW / 2 : (i / (data.length - 1)) * innerW);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(minValue + span * f));
  const rotateLabels = data.length > 8;
  // With more than ~8 points, labels are wider than the space between them even
  // rotated — thin them out instead of letting them overlap/clip, always keeping
  // the most recent point's label.
  const labelStep = data.length > 8 ? Math.ceil(data.length / 8) : 1;
  const showLabel = (i) => i % labelStep === 0 || i === data.length - 1;

  const lastRow = data[data.length - 1];
  const endLabels = seriesKeys
    .map((s) => ({ key: s.key, y: yFor(lastRow[s.key] || 0) }))
    .sort((a, b) => a.y - b.y);
  for (let i = 1; i < endLabels.length; i += 1) {
    endLabels[i].y = Math.max(endLabels[i].y, endLabels[i - 1].y + END_LABEL_GAP);
  }
  const endOverflow = endLabels.length ? endLabels[endLabels.length - 1].y - INNER_H : 0;
  if (endOverflow > 0) endLabels.forEach((label) => { label.y -= endOverflow; });
  const endLabelY = Object.fromEntries(endLabels.map((label) => [label.key, label.y]));

  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <div className="flex flex-wrap gap-4 items-center">
          {seriesKeys.length > 1 &&
            seriesKeys.map((s) => (
              <span key={s.key} className="flex items-center gap-1.5 text-sm font-semibold text-gray-700">
                <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
            ))}
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-sm font-bold whitespace-nowrap text-theme-color-1 hover:underline print:hidden"
        >
          {showTable ? "View as chart" : "View as table"}
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-base divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-3 py-2 text-sm font-bold text-left text-gray-700 uppercase">Month</th>
                {seriesKeys.map((s) => (
                  <th key={s.key} className="px-3 py-2 text-sm font-bold text-right text-gray-700 uppercase">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((row) => (
                <tr key={row.month || row.label}>
                  <td className="px-3 py-2 font-semibold text-gray-900">{row.label}</td>
                  {seriesKeys.map((s) => (
                    <td key={s.key} className="px-3 py-2 font-semibold text-right text-gray-700">
                      {valueFormatter(row[s.key] || 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative" ref={containerRef}>
          <svg viewBox={`0 0 ${viewW} ${VIEW_H}`} width={viewW} height={VIEW_H} className="w-full h-[220px] overflow-visible" role="img" aria-label={title}>
            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {yTicks.map((tick) => (
                <g key={tick}>
                  <line x1={0} x2={innerW} y1={yFor(tick)} y2={yFor(tick)} stroke="#e1e0d9" strokeWidth={1} />
                  <text x={-8} y={yFor(tick)} textAnchor="end" dominantBaseline="middle" fontSize={13} fontWeight={700} fill="#52514e">
                    {valueFormatter(tick)}
                  </text>
                </g>
              ))}
              {minValue < 0 && (
                <line x1={0} x2={innerW} y1={yFor(0)} y2={yFor(0)} stroke="#c3c2b7" strokeWidth={1} />
              )}

              {/* Hover hit targets — one per data point column */}
              {data.map((row, i) => {
                const bandW = data.length === 1 ? innerW : innerW / (data.length - 1);
                return (
                  <rect
                    key={row.month || row.label}
                    x={xFor(i) - bandW / 2}
                    y={0}
                    width={bandW}
                    height={INNER_H}
                    fill="transparent"
                    onMouseEnter={() => setHoverIndex(i)}
                    onMouseLeave={() => setHoverIndex(null)}
                    onFocus={() => setHoverIndex(i)}
                    onBlur={() => setHoverIndex(null)}
                    tabIndex={0}
                  />
                );
              })}

              {hoverIndex !== null && (
                <line
                  x1={xFor(hoverIndex)}
                  x2={xFor(hoverIndex)}
                  y1={0}
                  y2={INNER_H}
                  stroke="#c3c2b7"
                  strokeWidth={1}
                  pointerEvents="none"
                />
              )}

              {seriesKeys.map((s) => {
                const points = data.map((row, i) => [xFor(i), yFor(row[s.key] || 0)]);
                const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
                const last = points[points.length - 1];
                return (
                  <g key={s.key} pointerEvents="none">
                    <path d={d} fill="none" stroke={s.color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                    {points.map(([x, y], i) => (
                      <circle
                        key={i}
                        cx={x}
                        cy={y}
                        r={hoverIndex === i ? 5 : 3}
                        fill={s.color}
                        stroke="#fcfcfb"
                        strokeWidth={2}
                      />
                    ))}
                    <text x={last[0] + 6} y={endLabelY[s.key]} dominantBaseline="middle" fontSize={13} fontWeight={700} fill="#3a3934">
                      {valueFormatter(lastRow[s.key] || 0)}
                    </text>
                  </g>
                );
              })}

              {data.map((row, i) => showLabel(i) && (
                <text
                  key={row.month || row.label}
                  x={xFor(i)}
                  y={INNER_H + 18}
                  textAnchor={rotateLabels ? "end" : "middle"}
                  fontSize={13}
                  fontWeight={700}
                  fill="#3a3934"
                  transform={rotateLabels ? `rotate(-35 ${xFor(i)} ${INNER_H + 18})` : undefined}
                >
                  {row.label}
                </text>
              ))}
            </g>
          </svg>

        </div>
      )}

      {hoverIndex !== null && !showTable && (
        <ChartTooltip anchorRef={containerRef} xRatio={(hoverIndex + 0.5) / data.length}>
          <p className="mb-1 font-bold text-gray-900">{data[hoverIndex].label}</p>
          {seriesKeys.map((s) => (
            <p key={s.key} className="flex items-center gap-1.5 text-gray-700">
              <span className="inline-block w-2 h-0.5 rounded" style={{ backgroundColor: s.color }} />
              <span className="font-bold">{valueFormatter(data[hoverIndex][s.key] || 0)}</span>
              <span className="font-semibold text-gray-500">{s.label}</span>
            </p>
          ))}
        </ChartTooltip>
      )}
    </div>
  );
};

export default LineChart;
